import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang, useSeo } from "../i18n/LangContext";
import { about } from "../i18n/content";
import { Reveal } from "../components/site/Reveal";
import { PageHeader, FinalCta } from "../components/site/Sections";

const accents = ["#005F60", "#FFCC00", "#FF682F"];

export default function About() {
  const { lang } = useLang();
  useSeo("about");
  const a = about[lang];
  return (
    <>
      <PageHeader eyebrow={a.eyebrow} lines={a.lines} intro={a.intro} testId="about-header" />

      <section className="border-t border-[#121518]" data-testid="about-fronts">
        <div className="mx-auto max-w-[1400px] px-6 py-16 sm:px-10 lg:py-28">
          <Reveal><p className="f-serif max-w-3xl text-[clamp(1.6rem,3vw,2.6rem)] leading-[1.15]">{a.frontsLead}</p></Reveal>
          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {a.fronts.map((f, i) => (
              <Reveal key={f.t} delay={i * 0.1} className="h-full">
                <div className="flex h-full flex-col bg-white p-8 lg:p-10" style={{ borderTop: `3px solid ${accents[i]}` }} data-testid={`about-front-${i}`}>
                  <span className="f-serif text-sm italic text-[#4A525A]">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="f-serif mt-10 text-3xl leading-tight lg:text-4xl">{f.t}</h2>
                  <p className="mt-4 text-base leading-relaxed text-[#2E353B]">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0E1A1B] text-[#FAF8F5]" data-testid="about-distinct">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-6 py-20 sm:px-10 lg:grid-cols-12 lg:py-32">
          <Reveal className="lg:col-span-7">
            <p className="f-serif text-[clamp(1.9rem,4vw,3.4rem)] leading-[1.1] tracking-[-0.01em]">{a.distinct}</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9 lg:self-end">
            <p className="text-lg leading-relaxed text-[#E9E4DA]">{a.distinctBody}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto grid max-w-[1400px] gap-10 px-6 py-20 sm:px-10 lg:grid-cols-12 lg:py-32" data-testid="about-method">
        <Reveal className="lg:col-span-4">
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#005F60]">{a.methodLabel}</p>
          <div className="mt-6 flex gap-2" aria-hidden="true">
            {["#005F60", "#FF682F", "#FFCC00", "#005F60", "#FF682F"].map((c, i) => <span key={i} className="h-3 w-3" style={{ background: c }} />)}
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7 lg:col-start-6">
          <p className="f-serif text-xl leading-relaxed lg:text-2xl">{a.method}</p>
          <Link to="/metodo-construtor" className="btn-dark mt-10 inline-flex" data-testid="about-method-link">{a.methodLink} <ArrowRight size={16} /></Link>
        </Reveal>
      </section>

      <section className="border-t border-[#E2DDD5]" data-testid="about-closing">
        <div className="mx-auto max-w-[1400px] px-6 py-20 sm:px-10 lg:py-32">
          <Reveal><p className="max-w-2xl text-lg leading-relaxed text-[#2E353B]">{a.closing}</p></Reveal>
          <Reveal delay={0.1}>
            <p className="f-serif mt-8 text-[clamp(2.8rem,8vw,7.5rem)] italic leading-[0.95] tracking-[-0.03em] text-[#005F60]">{a.thesis}</p>
          </Reveal>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
